import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import config from 'garaje/config/environment';

// Bidirectional URL sync between this iframe and the carapace shell.
//
// Contract (must match carapace/src/integration/IntegrationHost.tsx and
// demo-mfe/src/shellBridge.ts):
//   shell -> mfe:   { type: "shell:route", path, source: "shell", seq }
//   mfe   -> shell: { type: "mfe:ready",        path, source: "mfe" }
//                   { type: "mfe:route-change", path, source: "mfe" }
//
// Path-equality invariant: window.location.pathname equals the shell's full
// pathname for the active leaf. No translation tables.
//
// Coexists with the unrelated `x:app:*` message protocol handled in
// app/pods/components/react-app/component.ts (which keys on data.event,
// not data.type) — both listeners see every message but each only acts on
// its own protocol shape.

interface ShellRouteMessage {
  type: 'shell:route';
  source: 'shell';
  path: string;
}

function isShellRouteMessage(data: unknown): data is ShellRouteMessage {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return d['type'] === 'shell:route' && d['source'] === 'shell' && typeof d['path'] === 'string';
}

export default class ShellBridgeService extends Service {
  @service declare router: RouterService;

  private installed = false;
  private shellOrigins: string[] = [];
  private outboundOrigin: string | null = null;
  private lastEmittedPath: string | null = null;

  /**
   * Wire up the postMessage listener and `routeDidChange` subscription, then
   * announce readiness to the shell. Idempotent on success; safe to call
   * again after a no-op early-exit (allows tests to re-trigger install once
   * window.parent / config have been stubbed).
   */
  install(): void {
    if (this.installed) return;
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;

    const origins = (config as { shellOrigins?: string[] }).shellOrigins ?? [];
    if (origins.length === 0) return;
    this.shellOrigins = origins;

    this.outboundOrigin = pickInitialOutboundOrigin(origins);

    window.addEventListener('message', this.onMessage);
    this.router.on('routeDidChange', this.onRouteDidChange);

    this.installed = true;
    this.lastEmittedPath = window.location.pathname;
    this.post({
      type: 'mfe:ready',
      path: window.location.pathname,
      source: 'mfe',
    });
  }

  willDestroy(): void {
    super.willDestroy();
    if (!this.installed) return;
    window.removeEventListener('message', this.onMessage);
    this.router.off('routeDidChange', this.onRouteDidChange);
    this.installed = false;
  }

  private post(data: object): void {
    if (!this.outboundOrigin) return;
    window.parent.postMessage(data, this.outboundOrigin);
  }

  private onMessage = (event: MessageEvent): void => {
    if (!this.shellOrigins.includes(event.origin)) return;
    if (event.source !== window.parent) return;

    this.outboundOrigin = event.origin;

    if (!isShellRouteMessage(event.data)) return;

    const path = event.data.path;
    if (path === window.location.pathname) return;

    this.lastEmittedPath = path;
    this.router.transitionTo(path);
  };

  private onRouteDidChange = (): void => {
    // `lastEmittedPath` is set to the requested path on inbound `shell:route`
    // and to the actual pathname on every emit. If pathname matches, we either
    // landed where the shell asked or already emitted this path — swallow the
    // echo. If pathname differs (route guard redirected us, or a previous
    // shell-driven transition aborted and the user then navigated somewhere
    // else), emit so the shell URL stays in sync with reality.
    const path = window.location.pathname;
    if (path === this.lastEmittedPath) return;

    this.lastEmittedPath = path;
    this.post({ type: 'mfe:route-change', path, source: 'mfe' });
  };
}

function pickInitialOutboundOrigin(allowed: string[]): string | null {
  try {
    if (document.referrer) {
      const refOrigin = new URL(document.referrer).origin;
      if (allowed.includes(refOrigin)) return refOrigin;
    }
  } catch {
    /* ignore */
  }
  return allowed[0] ?? null;
}

declare module '@ember/service' {
  interface Registry {
    'shell-bridge': ShellBridgeService;
  }
}
